import { useCallback, useEffect, useState } from "react";
import { GraduationCap, Hospital, TrainFront } from "lucide-react";
import PropertyMap from "@/components/PropertyMap";
import { api } from "@/lib/api";

const KINDS = {
  school: { label: "Schools", icon: GraduationCap, color: "#0f766e" },
  hospital: { label: "Hospitals", icon: Hospital, color: "#b91c1c" },
  metro: { label: "Metro / rail", icon: TrainFront, color: "#1d4ed8" },
};

export const NearbyPlaces = ({ property }) => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(["school", "hospital", "metro"]);
  const [state, setState] = useState("loading");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${property.property_id}/nearby`);
      setItems(data.items || []);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [property.property_id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (k) => setActive((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));
  const shown = items.filter((i) => active.includes(i.kind));

  return (
    <div data-testid="nearby-places">
      <h3 className="font-display text-lg">What's nearby</h3>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {[property.locality, property.city].filter(Boolean).join(", ")} · schools, hospitals and transit within 2.5 km
      </p>

      <div className="mt-4 flex flex-wrap gap-2.5">
        {Object.entries(KINDS).map(([k, v]) => {
          const on = active.includes(k);
          const count = items.filter((i) => i.kind === k).length;
          return (
            <button
              onClick={() => toggle(k)}
              key={k}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors duration-200 ${
                on ? "border-foreground bg-muted" : "border-border text-muted-foreground hover:border-foreground"
              }`}
              data-testid={`nearby-filter-${k}`}
            >
              <v.icon className="h-4 w-4" style={{ color: v.color }} /> {v.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        <PropertyMap items={[property]} places={shown} height="340px" />
      </div>

      {state === "loading" && <p className="mt-3 text-[13px] text-muted-foreground">Finding nearby places…</p>}
      {state === "error" && (
        <p className="mt-3 flex items-center gap-3 text-[13px] text-muted-foreground" data-testid="nearby-error">
          Nearby places are unavailable right now.
          <button
            onClick={() => {
              setState("loading");
              load();
            }}
            className="font-bold text-foreground underline"
            data-testid="nearby-retry-btn"
          >
            Try again
          </button>
        </p>
      )}
      {state === "ready" && shown.length === 0 && (
        <p className="mt-3 text-[13px] text-muted-foreground" data-testid="nearby-empty">
          No mapped places of these types within 2.5 km.
        </p>
      )}

      {shown.length > 0 && (
        <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="nearby-list">
          {shown.slice(0, 10).map((i) => {
            const K = KINDS[i.kind];
            return (
              <li key={i.id} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
                <K.icon className="h-4 w-4 shrink-0" style={{ color: K.color }} />
                <span className="flex-1 truncate text-[13px] font-semibold">{i.name}</span>
                <span className="text-[12px] text-muted-foreground">
                  {i.distance_m < 1000 ? `${i.distance_m} m` : `${(i.distance_m / 1000).toFixed(1)} km`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NearbyPlaces;
